import React from "react";
import styled from "styled-components";
import { Typography } from "@shared/ui/Typography";
import { Button } from "@shared/ui/Button";
import { Checkbox } from "@shared/ui/Checkbox";
import { Input } from "@shared/ui/Input";
import { Controller, useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { Trans, useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import { PageBackButton } from "@shared/ui/PageBackButton";
import { IApplicationFormStep1 } from "@entities/Product/Application/types";
import { useRouter } from "next/router";
import Link from "next/link";
import { routePaths } from "@shared/routes";
import { useUnit } from "effector-react";
import { sendVerificationCode } from "@shared/model";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  $applicationFormStep1,
  $applicationStep,
  setApplicationStep,
  submitApplicationStep1,
} from "@entities/Product/Application/model";
import { applicationValidationStep1Schema } from "@entities/Product/Application/utils";
import { $mainDocuments } from "@features/Layout/model";

export function ApplicationStep1() {
  const { t } = useTranslation();
  const { back } = useRouter();
  const docs = useUnit($mainDocuments);
  const isLoadingVerifyPhone = useUnit(sendVerificationCode.pending);
  const [step1data, submitStep1] = useUnit([
    $applicationFormStep1,
    submitApplicationStep1,
  ]);
  const [activeStep, setActiveStep] = useUnit([
    $applicationStep,
    setApplicationStep,
  ]);

  const methods = useForm<IApplicationFormStep1>({
    mode: "onChange",
    shouldUnregister: false,
    resolver: zodResolver(applicationValidationStep1Schema(t)),
    defaultValues: step1data,
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    try {
      submitStep1(data);
      await sendVerificationCode({
        phone: methods.getValues("phone").replace(/[^0-9]/g, ""),
      });
      setActiveStep(activeStep + 1);
    } catch (e) {
      methods.setError("phone", {
        message: t("Лимит попыток превышен, попробуйте через 30 минут"),
      });
    }
  });

  return (
    <StyledWrapper onSubmit={onSubmit}>
      <HorizontalFormField label={t("Номер телефона")}>
        <Controller
          name="phone"
          control={methods.control}
          render={({ field, fieldState }) => (
            <PatternFormat
              {...field}
              hasError={Boolean(fieldState.error?.message)}
              errorMessage={fieldState.error?.message}
              format="+7 (###) ###-##-##"
              allowEmptyFormatting
              mask="_"
              type="text"
              customInput={Input}
            />
          )}
        />
      </HorizontalFormField>
      <StyledBottom>
        <StyledPageBackButton
          onClick={() => back()}
          color="primary_red"
          label={t(`Вернуться назад`)}
        />
        <StyledButtonContent>
          <Button
            type="submit"
            disabled={!methods.formState.isValid || isLoadingVerifyPhone}
          >
            {t(`Получить SMS код`)}
          </Button>
          <StyledCheckbox>
            <Checkbox {...methods.register("agreement")} id="agreement" />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="agreement">
              <Typography>
                <Trans i18nKey="Нажимая на кнопку, я соглашаюсь на обработку Персональных данных и с Правилами пользования платформой">
                  Нажимая на кнопку, я соглашаюсь на обработку
                  <StyledPersonalDataLink
                    href={routePaths.main.article.personalData}
                    target="_blank"
                  >
                    Персональных данных
                  </StyledPersonalDataLink>
                  и c
                  <StyledLink href={docs?.terms?.url} target="_blank">
                    Правилами пользования
                  </StyledLink>
                  платформой
                </Trans>
              </Typography>
            </label>
          </StyledCheckbox>
        </StyledButtonContent>
      </StyledBottom>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.form`
  display: grid;
  grid-gap: 2.4rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 1.6rem;
  }
`;

const StyledBottom = styled.div`
  display: grid;
  margin-top: 1.6rem;
  grid-template-columns: 1.2fr 1.8fr;
  align-items: flex-start;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-template-columns: 1fr;
  }
`;

const StyledPageBackButton = styled(PageBackButton)`
  padding: 1rem 0;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    display: none;
  }
`;

const StyledButtonContent = styled.div`
  display: grid;
  grid-gap: 2rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 2.4rem;
  }
`;

const StyledCheckbox = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: flex-start;
`;

const StyledLink = styled.a`
  text-decoration: underline;
`;

const StyledPersonalDataLink = styled(Link)`
  text-decoration: underline;
`;
